import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Button, DialogContentText, Stack, TableCell } from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { TableWidgetV2, CustomFilterV2, DialogDefaultV2, ButtonMenuV2, UploadDialogV2 } from '../../../components/v2';
import * as selectorsControleCaixas from '../../../store/controleCaixa/reducer';
import * as actionsControleCaixas from '../../../store/controleCaixa/actions';
import * as actionsToast from '../../../store/toast/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as Roles from '../../../constants/role';
import { getMaxColumnLength } from '../../../utils';
import FormularioDespesas from './FormularioDespesas';
import FormularioReceita from './FormularioReceita';

const TIPO_DESPESA_OPTIONS = [
  { value: 'ALUGUEL_GARAGEM', label: 'Aluguel de Garagem' },
  { value: 'ALUGUEL_VEICULOS', label: 'Aluguel de Veículo' },
  { value: 'APORTE', label: 'Aporte' },
  { value: 'CONDOMINIO', label: 'Condomínio' },
  { value: 'DARF', label: 'Darf' },
  { value: 'ENERGIA_ELETRICA', label: 'Energia Elétrica' },
  { value: 'FOLHA_PAGAMENTO', label: 'Folha Pagamento' },
  { value: 'HONORARIOS_CONTABEIS', label: 'Honorários Contábeis' },
  { value: 'IMPOSTOS', label: 'Impostos' },
  { value: 'INTERNET', label: 'Internet' },
  { value: 'MANUTENCAO_DRONE', label: 'Manutenção Drone' },
  { value: 'MANUTENCAO_VEICULO', label: 'Manutenção Veículo' },
  { value: 'OUTROS', label: 'Outros' },
  { value: 'PAGAMENTO_DIGITACAO', label: 'Pagamento Digitação' },
  { value: 'PAGAMENTO_PRODUCAO', label: 'Pagamento Produção' },
  { value: 'PLANO_SAUDE', label: 'Plano de Saúde' },
  { value: 'SEGURO_CONTRA_ACIDENTE', label: 'Seguro Contra Acidente' },
  { value: 'TAXAS', label: 'Taxas' },
];

const STATUS_OPTIONS = [
  { value: 'A_LIQUIDAR', label: 'A Liquidar' },
  { value: 'LIQUIDADO', label: 'Liquidado' },
];

const ControleCaixaV2 = ({ controleCaixas, loadControleCaixas, deleteControleCaixa, authData, importControleCaixa, messageError, exportControleCaixa }) => {
  const [openDespesas, setOpenDespesas] = useState(false);
  const [openReceitas, setOpenReceitas] = useState(false);
  const [openImportDespesa, setOpenImportDespesa] = useState(false);
  const [openImportReceita, setOpenImportReceita] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [idDespesa, setIdDespesa] = useState(null);
  const [idReceita, setIdReceita] = useState(null);
  const [localFilter, setLocalFilter] = useState({
    inicio: moment().subtract(1, 'year').startOf('year').valueOf(),
    fim: moment().endOf('day').valueOf(),
  });

  useEffect(() => {
    loadControleCaixas(localFilter);
  }, [loadControleCaixas]);

  const isAdmin = authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);

  const onEdit = (id) => {
    const item = (controleCaixas || []).find((a) => a.id === id);
    if (!item) return;
    if (item.tipo === 'DESPESA') {
      setIdDespesa(id);
      setOpenDespesas(true);
    } else {
      setIdReceita(id);
      setOpenReceitas(true);
    }
  };

  const onClose = (isSave) => {
    if (isSave) {
      loadControleCaixas(localFilter);
    }
    setOpenDespesas(false);
    setOpenReceitas(false);
    setIdDespesa(null);
    setIdReceita(null);
  };

  const onImportDespesas = (files) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: 'binary' });
      if (workbook.SheetNames.includes('Despesas')) {
        const controles = [];
        const excelData = XLSX.utils.sheet_to_json(workbook.Sheets['Despesas'], { header: 1 }).slice(1);
        for (const row of excelData) {
          if (row.length === 0) continue;
          controles.push(row);
        }
        importControleCaixa({ controles }, 'despesas', () => { setOpenImportDespesa(false); loadControleCaixas(localFilter); });
      } else {
        messageError({ message: "Planilha inválida. Por favor, utilize a planilha 'Planilha Importação de Despesas' que se encontra na aba de Documentos!" });
      }
    };
    reader.onerror = () => messageError({ message: 'Erro ao abrir a planilha. Por favor, tente novamente!' });
    reader.readAsBinaryString(files[0]);
  };

  const onImportReceitas = (files) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: 'binary' });
      if (workbook.SheetNames.includes('Receitas')) {
        const controles = [];
        const excelData = XLSX.utils.sheet_to_json(workbook.Sheets['Receitas'], { header: 1 }).slice(1);
        for (const row of excelData) {
          if (row.length === 0) continue;
          controles.push(row);
        }
        importControleCaixa({ controles }, 'receitas', () => { loadControleCaixas(localFilter); setOpenImportReceita(false); });
      } else {
        messageError({ message: "Planilha inválida. Por favor, utilize a planilha 'Planilha Importação de Receitas' que se encontra na aba de Documentos!" });
      }
    };
    reader.onerror = () => messageError({ message: 'Erro ao abrir a planilha. Por favor, tente novamente!' });
    reader.readAsBinaryString(files[0]);
  };

  const renderRows = (r) => {
    const iconColor = r.tipo === 'DESPESA' ? '#CC0000' : 'green';
    return (
      <>
        <TableCell size="small">
          <MonetizationOnIcon sx={{ color: iconColor }} />
        </TableCell>
        <TableCell size="small">{moment(Number(r.dataDocumento)).format('DD/MM/YYYY')}</TableCell>
        <TableCell size="small">{r.valorLiquido != null ? r.valorLiquido.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) : '-'}</TableCell>
        <TableCell size="small">{r.status}</TableCell>
        <TableCell size="small">{r.formaPagamento}</TableCell>
        <TableCell size="small">{r.tipoDespesa}</TableCell>
        <TableCell size="medium" title={r.descricao}>{getMaxColumnLength(r.descricao)}</TableCell>
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {isAdmin && (
            <>
              <ButtonMenuV2
                title="Novo"
                options={[
                  { title: 'Despesa', onClick: () => setOpenDespesas(true) },
                  { title: 'Receita', onClick: () => setOpenReceitas(true) },
                ]}
              />
              <ButtonMenuV2
                title="Importar"
                options={[
                  { title: 'Despesa', onClick: () => setOpenImportDespesa(true) },
                  { title: 'Receita', onClick: () => setOpenImportReceita(true) },
                ]}
              />
            </>
          )}
          <Button variant="outlined" onClick={() => {}}>Filtros</Button>
        </Stack>
        <CustomFilterV2
          variant="dialog"
          filterData={localFilter}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            loadControleCaixas(filterData);
          }}
          fields={[
            { field: 'useDates' },
            { field: 'useSelectOption', key: 'tipoControle', label: 'Tipo', options: [{ id: 'DESPESA', nome: 'Despesa' }, { id: 'RECEITA', nome: 'Receita' }] },
            { field: 'useMultiSelectCustomOption', key: 'tipoDespesas', label: 'Tipo Despesa', options: TIPO_DESPESA_OPTIONS.map((o) => o.value) },
            { field: 'useSelectOption', key: 'formaPagamento', label: 'Forma Pagamento', options: [{ id: 'BANCO', nome: 'Banco' }, { id: 'CAIXA', nome: 'Caixa' }] },
            { field: 'useMultiSelectCustomOption', key: 'controleCaixaStatus', label: 'Status', options: STATUS_OPTIONS.map((o) => o.value) },
          ]}
        />
        <TableWidgetV2
          rows={controleCaixas || []}
          titles={['', 'Data da Emissão/Pagamento', 'Valor Líquido', 'Status', 'Forma Pagamento', 'Tipo Despesa', 'Descrição']}
          headerTitle="Controle de Caixa"
          renderRows={renderRows}
          hasAdd={false}
          showDelete={false}
          showEdit
          hasRowActions={isAdmin}
          onEdit={onEdit}
          headerActions={[{ title: 'Exportar', onClick: () => setOpenExport(true) }]}
        />
      </Stack>
      <FormularioDespesas id={idDespesa} open={openDespesas} onClose={onClose} />
      <FormularioReceita id={idReceita} open={openReceitas} onClose={onClose} />
      <UploadDialogV2
        open={openImportDespesa}
        onClose={() => setOpenImportDespesa(false)}
        onSave={onImportDespesas}
        acceptedFiles={['.xls', '.xlsx']}
        dialogTitle="Importar Despesa"
      />
      <UploadDialogV2
        open={openImportReceita}
        onClose={() => setOpenImportReceita(false)}
        onSave={onImportReceitas}
        acceptedFiles={['.xls', '.xlsx']}
        dialogTitle="Importar Receita"
      />
      <DialogDefaultV2
        open={openExport}
        handlerCancel={() => setOpenExport(false)}
        handlerConfirm={() => exportControleCaixa(localFilter, () => setOpenExport(false))}
        title="Confirmação"
        confirmMessage="Sim"
        cancelMessage="Não"
      >
        <DialogContentText>Deseja exportar todas as informações de receitas e despesas filtradas?</DialogContentText>
      </DialogDefaultV2>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  controleCaixas: selectorsControleCaixas.getControleCaixas(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionsControleCaixas, ...actionsToast }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ControleCaixaV2);
